import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Info as IconeInfo } from 'lucide-react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { api } from '../api/client';
import { fmtCHF } from '../utils/format';

/**
 * Coûts ateliers — liste plate comparable, sur l'activité réelle v2.1.
 *
 * Trois lectures complémentaires, sans score synthétique :
 *   · CHF / h-participant·e (tri par défaut) — coût d'une heure réellement
 *     délivrée : la colonne qui compare des ateliers de tailles différentes ;
 *   · occupation — le POURQUOI d'un coût unitaire élevé ;
 *   · coût total (barre proportionnelle) — où part l'argent en volume.
 *
 * Chaque valeur est expliquée par une infobulle sur son en-tête. Une colonne
 * par grandeur — rien de « rangé dans » la colonne prestation. Période par
 * dates réelles, préréglages en un clic ; défaut = dernier semestre RÉVOLU
 * (un semestre en cours n'a que quelques semaines de présences et fausse
 * toute comparaison).
 */

interface Ligne {
  offeringId: number;
  nom: string;
  categorie: string;
  domaine: string | null;
  type: string;
  lieu: string | null;
  encadrant: string | null;
  seances: number;
  dureeMoyenne: number;
  heuresFacePublic: number;
  heuresEncadrement: number;
  presenceMoyenne: number | null;
  capacite: number | null;
  occupation: number | null;
  heuresParticipants: number;
  heuresInvisibles: number;
  heuresInvisiblesParSeance: number;
  coutFace: number | null;
  coutInvisible: number | null;
  presAiMoyenne: number | null;
  presAutresMoyenne: number | null;
  coutAi: number | null;
  coutRi: number | null;
  seancesParJour: Record<string, number>;
  tauxHoraire: number | null;
  cout: number | null;
  coutParSeance: number | null;
  coutParHeureParticipant: number | null;
  sansEncadrant: boolean;
}
interface Reponse {
  de: string; a: string;
  tauxChargesSociales: number; heuresAnnuelles100: number;
  normes: Record<string, number>;
  lignes: Ligne[];
  nonTypees: { nom: string; categorie: string; heures: number }[];
  totaux: {
    seances: number; heuresFacePublic: number; heuresEncadrement: number;
    heuresParticipants: number; cout: number;
    lignesSansCout: number; lignesNonTypees: number;
    coutParHeureParticipantMedian: number | null;
  };
  disponible: boolean;
}

type Cle = 'nom' | 'encadrant' | 'lieu' | 'categorie' | 'domaine' | 'type' | 'seances' | 'dureeMoyenne'
  | 'heuresInvisiblesParSeance' | 'presenceMoyenne' | 'occupation' | 'presAiMoyenne'
  | 'heuresFacePublic' | 'heuresInvisibles' | 'heuresEncadrement'
  | 'tauxHoraire' | 'coutFace' | 'coutInvisible' | 'coutParSeance'
  | 'coutParHeureParticipant' | 'cout' | 'coutAi' | 'coutRi';

const fmtH = (v: number) => `${v.toLocaleString('fr-CH')} h`;
const fmtPct = (v: number) => `${Math.round(v * 100)} %`;
const iso = (d: Date) => d.toISOString().slice(0, 10);

/** Dernier semestre entièrement révolu — le défaut honnête pour comparer. */
function dernierSemestreRevolu(): { de: string; a: string } {
  const now = new Date();
  const y = now.getFullYear();
  return now.getMonth() >= 6
    ? { de: `${y}-01-01`, a: `${y}-06-30` }
    : { de: `${y - 1}-07-01`, a: `${y - 1}-12-31` };
}

function occBadge(o: number | null) {
  if (o == null) return null;
  const cls = o < 0.4 ? 'badge badge-err badge-sm' : o < 0.7 ? 'badge badge-warn badge-sm' : 'badge badge-ok badge-sm';
  return <span className={cls}>{fmtPct(o)}</span>;
}

function Info({ texte }: { texte: string }) {
  const [open, setOpen] = useState(false);
  return (
    <Tooltip.Root open={open} onOpenChange={setOpen}>
      <Tooltip.Trigger asChild>
        <button type="button" aria-label="Explication du calcul"
                onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
                style={{ background: 'none', border: 'none', padding: 0, marginLeft: 3, cursor: 'pointer',
                         color: 'var(--ink-3)', verticalAlign: 'middle', lineHeight: 0 }}>
          <IconeInfo size={12} />
        </button>
      </Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content data-radix-tooltip-content side="top" sideOffset={6}
                         style={{ maxWidth: 340, whiteSpace: 'pre-line' }}
                         onPointerDownOutside={() => setOpen(false)}>{texte}</Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}

const EXPLICATIONS: Record<Cle, string> = {
  nom: "L'offre de la base canonique v2.1 (offering). Séances non annulées de la période ; les stages (Placement) sont hors modèle — leur capacité est côté personnel.",
  encadrant: "Encadrant·e du planning v2.1 le plus récent de l'offre. Son taux horaire vient des salaires budget via le lien employé ↔ v2.1 ; un·e intervenant·e externe ou non lié·e = heures affichées, CHF « — ».",
  lieu: 'Lieu de la salle du planning v2.1 le plus récent.',
  categorie: 'Catégorie v2.1 : Atelier, Espace ou Suivi individuel.',
  domaine: 'Domaine v2.1 (Créatif, Corporel, Sortie, Évolution…) — la taxonomie pédagogique.',
  type: "Type du modèle de coûts (Boza V51) : il fixe les coefficients de préparation, débriefing, admin et intervision appliqués aux normes horaires.",
  seances: 'Nombre de séances non annulées dans la période choisie.',
  dureeMoyenne: "Durée moyenne face public d'une séance (heures).",
  presenceMoyenne: "Présences moyennes par séance (statut « present » dans v2.1) / capacité idéale de l'offre (à défaut, capacité max).",
  occupation: 'Présence moyenne ÷ capacité. Rouge < 40 %, orange 40–70 %, vert ≥ 70 %. Une occupation faible explique un coût unitaire élevé.',
  heuresEncadrement: "TOTAL des heures payées = h avec participant·es + h préparation etc.\nDétail du travail invisible (modèle Boza V51, par séance) : préparation (1 h × coeff. du type — Topo ×1.5, Sortie ×2, Corporel ×0.25), débriefing (20 min × coeff.), admin (8 min × coeff. × présence moyenne réelle), rangement (10 min), + intervision (1.42 h) et orientation (0.75 h) par mois.",
  tauxHoraire: "Taux horaire chargé de l'encadrant·e : nominal mensuel moyen × 13 (13e) × (1 + charges sociales) ÷ 2008 h/an à 100 %.\nLE MÊME taux paie TOUTES les heures : celles avec les participant·es ET la préparation/débriefing/admin/rangement — pas seulement le temps de présence.",
  coutParSeance: "Calcul : Coût total ÷ nombre de séances.\nEx. : 4'508 CHF ÷ 25 séances = 180 CHF la séance.",
  coutParHeureParticipant: "Calcul : Coût total ÷ heures-participant·es réellement délivrées.\nHeures-participant·es = Σ des heures de présence individuelles (statut « present » dans v2.1) : 5 personnes restées 2 h = 10 h délivrées.\nEx. : 4'508 CHF ÷ 156 h délivrées = 28.90 CHF/h-part.\nC'est LE chiffre comparable entre ateliers de tailles différentes ; l'occupation explique les extrêmes.",
  heuresInvisiblesParSeance: "Travail INVISIBLE par séance = (h d'encadrement − h face public) ÷ séances.\nC'est la préparation + débriefing + admin + rangement + la part mensuelle d'intervision/orientation — tout ce qui se fait sans le public.\nEx. : un Topo d'1 h porte ~2.3 h invisibles ; une Sortie de 7 h ~2.6 h.",
  heuresFacePublic: "Heures d'encadrement passées AVEC les participant·es : la durée face public des séances (v2.1, sessions non annulées).",
  heuresInvisibles: "Heures payées SANS les participant·es : préparation + débriefing + admin × présence + rangement (normes × coefficients du type) + intervision/orientation mensuelles.\nC'est le travail autour des séances — payé au même taux.",
  coutFace: "CHF du temps AVEC les participant·es.\nCalcul : h avec part. × taux horaire.\nEx. : 119 h × 48.46 = 5'767 CHF.",
  coutInvisible: "CHF du travail SANS les participant·es (préparation etc.).\nCalcul : h prép. etc. × taux horaire.\nEx. : 24.4 h × 48.46 = 1'181 CHF.",
  cout: "Coût RH total = CHF avec participant·es + CHF préparation etc.\n(= h payées totales × taux horaire chargé réel de l'encadrant·e).\nDonc : un atelier coûte selon QUI l'anime, COMBIEN de temps il exige autour des séances, et COMBIEN de séances ont eu lieu.\nNon compris : matériel, loyers/salles, structure.\nLe fond coloré est une barre : largeur = ce coût ÷ le plus gros coût affiché.",
  presAiMoyenne: "RÉPARTITION DES PARTICIPANT·ES (pas du coût) : présences moyennes par séance sous mandat OAI vs tous les autres mandants. Bande bleu = OAI, gris = autres. La répartition du COÛT est dans les colonnes Coût OAI / Coût RI.",
  coutAi: "RÉPARTITION DU COÛT (CHF) : part du coût total portée par l'OAI, au prorata des heures réellement délivrées à des mandats AI (heures OAI ÷ heures totales).",
  coutRi: "RÉPARTITION DU COÛT (CHF) : part portée par le RI/CSR, même prorata d'heures délivrées. Coût OAI + Coût RI ne somment pas toujours au total : le solde revient aux autres familles (SISP, EVAM…).",
};

/**
 * Balance OAI / autres : chaque nombre est posé de SON côté de la barre —
 * OAI (bleu) à gauche, autres (gris) à droite, segments proportionnels.
 * Se lit comme une balance : 3,3 ▓▓▓▓▓░░ 1,6.
 */
function BandePublics({ ai, autres }: { ai: number; autres: number }) {
  const total = ai + autres;
  if (total <= 0) return <span className="text-ink-3">—</span>;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 110 }}>
      <span className="tabular-nums" style={{ fontSize: 12, color: 'var(--accent, #4a7dbd)', minWidth: 24, textAlign: 'right' }}>
        {ai.toLocaleString('fr-CH')}
      </span>
      <span aria-hidden style={{ flex: 1, display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', background: 'var(--line)' }}>
        <span style={{ width: `${(ai / total) * 100}%`, background: 'var(--accent, #4a7dbd)' }} />
        <span style={{ width: `${(autres / total) * 100}%`, background: 'var(--ink-3)' }} />
      </span>
      <span className="tabular-nums text-ink-3" style={{ fontSize: 12, minWidth: 24 }}>
        {autres.toLocaleString('fr-CH')}
      </span>
    </div>
  );
}

/** Heatmap façon GitHub : la période en semaines × jours, une cellule = un jour. */
function HeatmapSeances({ de, a, jours }: { de: string; a: string; jours: Record<string, number> }) {
  const debut = new Date(de + 'T00:00:00Z');
  const fin = new Date(a + 'T00:00:00Z');
  // Aligner sur le lundi précédant le début.
  const premier = new Date(debut);
  premier.setUTCDate(premier.getUTCDate() - ((premier.getUTCDay() + 6) % 7));
  const semaines: { cle: string; n: number | null }[][] = [];
  const max = Math.max(1, ...Object.values(jours));
  for (let d = new Date(premier); d <= fin; ) {
    const semaine: { cle: string; n: number | null }[] = [];
    for (let j = 0; j < 7; j++) {
      const cle = d.toISOString().slice(0, 10);
      semaine.push({ cle, n: d < debut || d > fin ? null : (jours[cle] ?? 0) });
      d = new Date(d); d.setUTCDate(d.getUTCDate() + 1);
    }
    semaines.push(semaine);
  }
  const mois = new Set<string>();
  const couleur = (n: number | null) => {
    if (n == null) return 'transparent';
    if (n === 0) return 'var(--line)';
    const alpha = 0.35 + 0.65 * (n / max);
    return `color-mix(in srgb, var(--accent, #4a7dbd) ${Math.round(alpha * 100)}%, var(--line))`;
  };
  return (
    <div>
      <div style={{ display: 'flex', gap: 2 }}>
        {semaines.map((sem, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {sem.map((c) => (
              <span key={c.cle} title={c.n != null && c.n > 0 ? `${c.cle} · ${c.n} séance${c.n > 1 ? 's' : ''}` : c.cle}
                    style={{ width: 7, height: 7, borderRadius: 2, background: couleur(c.n) }} />
            ))}
          </div>
        ))}
      </div>
      <div className="text-ink-3" style={{ fontSize: 10, marginTop: 4 }}>{de} → {a} · lignes = Lu→Di · plus foncé = plus de séances</div>
    </div>
  );
}

export function CoutAteliersPage() {
  const defaut = dernierSemestreRevolu();
  const [de, setDe] = useState(defaut.de);
  const [a, setA] = useState(defaut.a);

  const [data, setData] = useState<Reponse | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [chargement, setChargement] = useState(true);

  const [selType, setSelType] = useState<string | null>(null);
  const [selCat, setSelCat] = useState<string | null>(null);
  const [recherche, setRecherche] = useState('');
  const [tri, setTri] = useState<Cle>('coutParHeureParticipant');
  const [desc, setDesc] = useState(true);

  const charger = useCallback(() => {
    if (!de || !a || de > a) return;
    setChargement(true);
    api.get<Reponse>(`/cout-ateliers?de=${de}&a=${a}`)
      .then((r: Reponse) => { setData(r); setErreur(null); })
      .catch((e: Error) => setErreur(e?.message ?? 'Erreur de chargement'))
      .finally(() => setChargement(false));
  }, [de, a]);
  useEffect(charger, [charger]);

  // Préréglages de période : chacun n'est qu'un couple de dates — les champs
  // Du/Au restent la vérité et sont librement éditables.
  const annee = Number((de || iso(new Date())).slice(0, 4));
  const presets = useMemo(() => {
    const now = new Date();
    const douzeMois = new Date(now); douzeMois.setFullYear(now.getFullYear() - 1);
    return [
      { nom: `S1 ${annee}`, de: `${annee}-01-01`, a: `${annee}-06-30` },
      { nom: `S2 ${annee}`, de: `${annee}-07-01`, a: `${annee}-12-31` },
      { nom: `Année ${annee}`, de: `${annee}-01-01`, a: `${annee}-12-31` },
      { nom: '12 derniers mois', de: iso(douzeMois), a: iso(now) },
    ];
  }, [annee]);

  const types = useMemo(() => {
    if (!data) return [] as { type: string; cout: number }[];
    const m = new Map<string, number>();
    for (const l of data.lignes) m.set(l.type, (m.get(l.type) ?? 0) + (l.cout ?? 0));
    return [...m.entries()].map(([type, cout]) => ({ type, cout })).sort((x, y) => y.cout - x.cout);
  }, [data]);

  const lignes = useMemo(() => {
    if (!data) return [];
    const q = recherche.trim().toLowerCase();
    let ls = data.lignes.filter((l) =>
      (selType == null || l.type === selType) &&
      (selCat == null || l.categorie === selCat) &&
      (!q || l.nom.toLowerCase().includes(q) || (l.encadrant ?? '').toLowerCase().includes(q) || (l.lieu ?? '').toLowerCase().includes(q))
    );
    const dir = desc ? -1 : 1;
    // La colonne « Prés. OAI/aut. » se trie sur la PART OAI (ratio), pas sur
    // le nombre absolu — 2/2 OAI passe devant 3/9.
    const valeur = (l: Ligne): string | number | null => {
      if (tri === 'presAiMoyenne') {
        const tot = (l.presAiMoyenne ?? 0) + (l.presAutresMoyenne ?? 0);
        return tot > 0 ? (l.presAiMoyenne ?? 0) / tot : null;
      }
      return l[tri];
    };
    ls = [...ls].sort((x, y) => {
      const vx = valeur(x), vy = valeur(y);
      if (vx == null && vy == null) return 0;
      if (vx == null) return 1;            // nuls toujours en fin, quel que soit le sens
      if (vy == null) return -1;
      if (typeof vx === 'string') return dir * vx.localeCompare(vy as string, 'fr');
      return dir * ((vx as number) - (vy as number));
    });
    return ls;
  }, [data, selType, selCat, recherche, tri, desc]);

  const maxCout = useMemo(() => Math.max(1, ...lignes.map((l) => l.cout ?? 0)), [lignes]);
  const totalFiltre = useMemo(() => lignes.reduce((s, l) => s + (l.cout ?? 0), 0), [lignes]);

  const trier = (c: Cle) => {
    if (tri === c) setDesc(!desc);
    else { setTri(c); setDesc(!['nom', 'encadrant', 'lieu', 'categorie', 'domaine', 'type'].includes(c)); }
  };
  const Th = ({ c, children, num = true }: { c: Cle; children: React.ReactNode; num?: boolean }) => (
    <th className={num ? 'cell-num' : undefined} onClick={() => trier(c)}
        style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
      {children}
      <Info texte={EXPLICATIONS[c]} />
      {tri === c && (desc ? <ArrowDown size={11} style={{ marginLeft: 2 }} /> : <ArrowUp size={11} style={{ marginLeft: 2 }} />)}
    </th>
  );

  const nombre = (v: number | null, suffixe = '') =>
    v != null ? <span className="tabular-nums">{v.toLocaleString('fr-CH')}{suffixe}</span> : <span className="text-ink-3">—</span>;

  return (
    <Tooltip.Provider>
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Coûts ateliers</h1>
          <p className="page-subtitle">Encadrement aux normes (matrice Boza) × activité réelle v2.1 — lecture seule</p>
        </div>
      </div>

      {/* ── Période : dates réelles + préréglages ── */}
      <div className="toolbar">
        <div className="flex items-center gap-8">
          <label className="form-label" htmlFor="ca-de" style={{ margin: 0 }}>Du</label>
          <input id="ca-de" type="date" className="form-input" style={{ width: 150 }} value={de} max={a}
                 onChange={(e) => setDe(e.target.value)} />
          <label className="form-label" htmlFor="ca-a" style={{ margin: 0 }}>au</label>
          <input id="ca-a" type="date" className="form-input" style={{ width: 150 }} value={a} min={de}
                 onChange={(e) => setA(e.target.value)} />
        </div>
        <div className="filter-chips">
          {presets.map((p) => (
            <span key={p.nom} className={`chip${de === p.de && a === p.a ? ' active' : ''}`}
                  onClick={() => { setDe(p.de); setA(p.a); }}>{p.nom}</span>
          ))}
          <select className="sort-select" value={annee} title="Année des préréglages"
                  onChange={(e) => { const y = Number(e.target.value); const dy = Number(de.slice(0, 4)); setDe(`${y}${de.slice(4)}`); setA(`${Number(a.slice(0, 4)) + (y - dy)}${a.slice(4)}`); }}>
            {[2025, 2026, 2027].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div className="search-wrapper">
          <input className="search-input" placeholder="Atelier, encadrant·e, lieu…" value={recherche} onChange={(e) => setRecherche(e.target.value)} />
        </div>
      </div>

      {erreur && <div className="card mb-16 p-16 text-err">{erreur}</div>}

      {data && (
        <>
          {/* ── Repères ── */}
          <div className="kpi-row">
            <div className="kpi-card">
              <span className="kpi-card__value">{fmtCHF(data.totaux.cout)}</span>
              <span className="kpi-card__label">coût d'encadrement chiffré<Info texte={EXPLICATIONS.cout} /></span>
            </div>
            <div className="kpi-card">
              <span className="kpi-card__value">{data.totaux.coutParHeureParticipantMedian != null ? `${data.totaux.coutParHeureParticipantMedian.toLocaleString('fr-CH')} CHF` : '—'}</span>
              <span className="kpi-card__label">médiane CHF / h-participant·e<Info texte={EXPLICATIONS.coutParHeureParticipant} /></span>
            </div>
            <div className="kpi-card">
              <span className="kpi-card__value">{fmtH(data.totaux.heuresEncadrement)}</span>
              <span className="kpi-card__label">h d'encadrement · {fmtH(data.totaux.heuresParticipants)} délivrées<Info texte={EXPLICATIONS.heuresEncadrement} /></span>
            </div>
            <div className="kpi-card">
              <span className="kpi-card__value">{data.totaux.lignesSansCout}</span>
              <span className="kpi-card__label">lignes sans CHF<Info texte={EXPLICATIONS.encadrant} /></span>
            </div>
          </div>

          {/* ── Filtres ── */}
          <div className="filter-pills">
            <span className={`chip${selType == null ? ' active' : ''}`} onClick={() => setSelType(null)}>Tous types</span>
            {types.map((t) => (
              <span key={t.type} className={`chip${selType === t.type ? ' active' : ''}`} onClick={() => setSelType(selType === t.type ? null : t.type)}>
                {t.type} · {fmtCHF(t.cout)}
              </span>
            ))}
          </div>
          <div className="filter-pills">
            {['Atelier', 'Espace', 'Suivi individuel'].map((c) => (
              <span key={c} className={`chip${selCat === c ? ' active' : ''}`} onClick={() => setSelCat(selCat === c ? null : c)}>{c}</span>
            ))}
            {(selType != null || selCat != null || recherche) && (
              <span className="chip" onClick={() => { setSelType(null); setSelCat(null); setRecherche(''); }}>Réinitialiser</span>
            )}
          </div>

          {/* ── La liste, plate et triable — une colonne par grandeur ── */}
          <div className="card">
            <div>
              <table className="table table-compact grille-couts">
                <thead>
                  <tr>
                    <Th c="nom" num={false}>Prestation ({lignes.length})</Th>
                    <Th c="encadrant" num={false}>Animateur·trice</Th>
                    <Th c="lieu" num={false}>Lieu</Th>
                    <Th c="categorie" num={false}>Catégorie</Th>
                    <Th c="domaine" num={false}>Domaine</Th>
                    <Th c="type" num={false}>Type</Th>
                    <Th c="seances">Séances</Th>
                    <Th c="dureeMoyenne">h/séance</Th>
                    <Th c="heuresInvisiblesParSeance">h invis./séance</Th>
                    <Th c="presenceMoyenne">Prés. moy.</Th>
                    <Th c="presAiMoyenne">Prés. OAI/aut.</Th>
                    <Th c="occupation">Occup.</Th>
                    <Th c="heuresFacePublic">H avec part.</Th>
                    <Th c="heuresInvisibles">H prép. etc.</Th>
                    <Th c="heuresEncadrement">H payées</Th>
                    <Th c="tauxHoraire">Taux CHF/h</Th>
                    <Th c="coutFace">CHF avec part.</Th>
                    <Th c="coutInvisible">CHF prép. etc.</Th>
                    <Th c="coutParSeance">CHF/séance</Th>
                    <Th c="coutParHeureParticipant">CHF/h-part.</Th>
                    <Th c="cout">Coût RH total</Th>
                    <Th c="coutAi">Coût OAI</Th>
                    <Th c="coutRi">Coût RI</Th>
                  </tr>
                </thead>
                <tbody>
                  {lignes.map((l) => (
                    <tr key={l.offeringId}>
                      <td className="cell-nom" title={l.nom}
                          style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.nom}</td>
                      <td title={l.encadrant ?? ''} style={{ maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.encadrant ?? <span className="text-ink-3">inconnu·e</span>}</td>
                      <td title={l.lieu ?? ''} style={{ maxWidth: 95, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.lieu ?? <span className="text-ink-3">—</span>}</td>
                      <td>{l.categorie}</td>
                      <td>{l.domaine ?? <span className="text-ink-3">—</span>}</td>
                      <td><span className="badge badge-plain badge-sm">{l.type}</span></td>
                      <td className="cell-num">
                        <Tooltip.Root delayDuration={150}>
                          <Tooltip.Trigger asChild>
                            <span className="tabular-nums" style={{ borderBottom: '1px dotted var(--line-strong)', cursor: 'help' }}>{l.seances}</span>
                          </Tooltip.Trigger>
                          <Tooltip.Portal>
                            <Tooltip.Content data-radix-tooltip-content side="right" sideOffset={6} style={{ maxWidth: 'none' }}>
                              <HeatmapSeances de={data.de} a={data.a} jours={l.seancesParJour} />
                            </Tooltip.Content>
                          </Tooltip.Portal>
                        </Tooltip.Root>
                      </td>
                      <td className="cell-num">{nombre(l.dureeMoyenne)}</td>
                      <td className="cell-num">{nombre(l.heuresInvisiblesParSeance)}</td>
                      <td className="cell-num">{l.presenceMoyenne != null ? <span className="tabular-nums">{l.presenceMoyenne.toLocaleString('fr-CH')}{l.capacite ? ` / ${l.capacite}` : ''}</span> : <span className="text-ink-3">—</span>}</td>
                      <td className="cell-num">{l.presAiMoyenne != null && l.presAutresMoyenne != null ? <BandePublics ai={l.presAiMoyenne} autres={l.presAutresMoyenne} /> : <span className="text-ink-3">—</span>}</td>
                      <td className="cell-num">{occBadge(l.occupation) ?? <span className="text-ink-3">—</span>}</td>
                      <td className="cell-num">{nombre(l.heuresFacePublic, ' h')}</td>
                      <td className="cell-num">{nombre(l.heuresInvisibles, ' h')}</td>
                      <td className="cell-num">{nombre(l.heuresEncadrement, ' h')}</td>
                      <td className="cell-num">{nombre(l.tauxHoraire)}</td>
                      <td className="cell-num">{nombre(l.coutFace)}</td>
                      <td className="cell-num">{nombre(l.coutInvisible)}</td>
                      <td className="cell-num">{nombre(l.coutParSeance)}</td>
                      <td className="cell-num"><strong>{nombre(l.coutParHeureParticipant)}</strong></td>
                      <td className="cell-num tabular-nums" style={{ minWidth: 110, background: l.cout != null
                            ? `linear-gradient(90deg, color-mix(in srgb, var(--accent, #4a7dbd) 16%, transparent) ${Math.max(3, (l.cout / maxCout) * 100)}%, transparent ${Math.max(3, (l.cout / maxCout) * 100)}%)`
                            : undefined }}>
                        {l.cout != null ? fmtCHF(l.cout) : <span className="text-ink-3">h seules</span>}
                      </td>
                      <td className="cell-num">{nombre(l.coutAi)}</td>
                      <td className="cell-num">{nombre(l.coutRi)}</td>
                    </tr>
                  ))}
                  {lignes.length === 0 && <tr><td colSpan={23} className="text-center text-ink-3 p-16">Aucune prestation sur cette période avec ces filtres.</td></tr>}
                </tbody>
                {lignes.length > 0 && (
                  <tfoot>
                    <tr>
                      <td colSpan={20} style={{ textAlign: 'right' }} className="text-ink-2">Total de la sélection</td>
                      <td className="cell-num"><strong className="tabular-nums">{fmtCHF(totalFiltre)}</strong></td>
                      <td className="cell-num"><strong className="tabular-nums">{fmtCHF(lignes.reduce((s, l) => s + (l.coutAi ?? 0), 0))}</strong></td>
                      <td className="cell-num"><strong className="tabular-nums">{fmtCHF(lignes.reduce((s, l) => s + (l.coutRi ?? 0), 0))}</strong></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          {data.nonTypees.length > 0 && (
            <div className="card mt-16 p-16">
              <strong>Prestations actives non typées ({data.nonTypees.length})</strong>
              <p className="text-ink-3">Leurs heures ne figurent pas ci-dessus — à ajouter au mapping (scripts/seed-cout-ateliers-v21.ts).</p>
              {data.nonTypees.map((n) => <div key={n.nom}>{n.nom} <span className="text-ink-3">({n.categorie} · {fmtH(n.heures)})</span></div>)}
            </div>
          )}

          <p className="text-ink-3" style={{ fontSize: 12, marginTop: 12 }}>
            <strong>Coût RH uniquement</strong> (décision opérateur — l'allocation des loyers est retirée en attendant des
            données de salles fiables) : h payées = h AVEC participant·es (durée face public v2.1) + h préparation/débriefing/
            admin/rangement/intervision (normes Boza × coefficients du type), le tout × le taux horaire chargé RÉEL de
            l'encadrant·e ({Math.round(data.tauxChargesSociales * 100)} % charges sociales, base {data.heuresAnnuelles100} h/an). <strong>Non compris :</strong> matériel, loyers/salles,
            structure. Sessions v2.1 non annulées, hors Placement. Le ⓘ de chaque colonne montre son calcul.
          </p>
        </>
      )}
      {chargement && !data && <div className="text-ink-3 p-16">Calcul en cours…</div>}
    </div>
    </Tooltip.Provider>
  );
}
